import {
  Button,
  Divider,
  Flex,
  // Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  VStack,
} from '@chakra-ui/react';
import { Formik, Form, FormikProps } from 'formik';
import * as _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo } from 'react';

import { finleyAssertUnreachable } from '@finley-technologies/common';
import {
  Util,
  Bank,
  CreditAgreement,
  DataTapeField,
  typeguards,
} from '@finley-technologies/finley-types';
import { getSanitizedDataTapeField } from '@finley-technologies/tape-sanitizer';

import {
  InputFormField,
  SelectFormField,
  TextareaFormField,
} from 'packages/common-frontend/components/FormField';
import { SelectWithCustomOption } from 'packages/common-frontend/components/SelectWithCustomOption';

const generateDateFormats = (): { label: string; value: string }[] => {
  // cons year part options
  const commonYearOptions = ['yy', 'yyyy'];
  const commonMonthOptions = ['MM' /* M , 'MMM', 'MMMM' */];
  const commonDayOptions = ['dd'];
  const separatorOptions = ['-', '/', '.'];

  // create all possible combinations using above options
  const commonDateFormats = _.flatMap(commonYearOptions, (yearOption) =>
    _.flatMap(commonMonthOptions, (monthOption) =>
      _.flatMap(commonDayOptions, (dayOption) =>
        _.flatMap(separatorOptions, (separator) => {
          return [
            `${yearOption}${separator}${monthOption}${separator}${dayOption}`,
            `${monthOption}${separator}${dayOption}${separator}${yearOption}`,
            `${dayOption}${separator}${monthOption}${separator}${yearOption}`,
          ];
        }),
      ),
    ),
  );

  const formats = [
    'MMMM d, y', // this format is for illustration purposes.
    // to see what every character means, uncomment the following lines
    // 'LL',
    // 'LLL',
    // 'LLLL',
    // 'M',
    // 'MM',
    // 'MMM',
    // 'MMMM',
    // 'd',
    // 'dd',
    // 'y',
    // 'yy',
    // 'yyyy',
    // 'c',
    // 'cc',
    // 'ccc',
    // 'cccc',
    ...commonDateFormats,
  ];

  return formats.map((format) => ({
    label: `${DateTime.fromObject({
      year: 1999,
      month: 1,
      day: 31,
    }).toFormat(format)} (${format})`,
    value: format,
  }));
};

type DefaultToValuesProps = {
  formProps: FormikProps<CreditAgreement.DataTapeField.BaseConfig>;
};
export const DefaultToValues = ({ formProps }: DefaultToValuesProps) => {
  const { values } = formProps;
  const { type } = values;

  switch (type) {
    case 'string':
    case 'integer':
    case 'float':
    case 'boolean':
    case 'currency':
    case 'date':
    case 'dateTime':
    case 'percentage':
      return (
        <InputFormField
          type="text"
          propName="defaultTo"
          label="Default To"
          formProps={formProps}
          value={values.defaultTo}
          placeholder="Enter defaultTo"
        />
      );
    case 'optionalString':
    case 'optionalInteger':
    case 'optionalFloat':
    case 'optionalBoolean':
    case 'optionalCurrency':
    case 'optionalDate':
    case 'optionalDateTime':
    case 'optionalPercentage':
      return null;
    default:
      return finleyAssertUnreachable(type);
  }
};

const allFieldNames = [
  'field',
  'description',
  'type',
  'externalBooleanTrue',
  'currencyCode',
  'externalDateFormat',
  'externalDateTimeFormat',
  'defaultTz',
  'defaultTo',
];

const getTypeSpecificFieldNames = (
  type: CreditAgreement.DataTapeField.Type,
): string[] => {
  const commonFieldNames = ['field', 'description', 'type'];

  const fieldNames = [...commonFieldNames];
  switch (type) {
    case 'boolean':
    case 'optionalBoolean':
      fieldNames.push('externalBooleanTrue');
      break;
    case 'currency':
    case 'optionalCurrency':
      fieldNames.push('currencyCode');
      break;
    case 'date':
    case 'optionalDate':
      fieldNames.push('externalDateFormat');
      break;
    case 'dateTime':
    case 'optionalDateTime':
      fieldNames.push('externalDateTimeFormat', 'defaultTz');
      break;
    case 'string':
    case 'optionalString':
    case 'integer':
    case 'optionalInteger':
    case 'float':
    case 'optionalFloat':
    case 'percentage':
    case 'optionalPercentage':
      break;
    default:
      return finleyAssertUnreachable(type);
  }

  // now add 'defaultTo' to the list
  // for non-optional fields.
  switch (type) {
    case 'string':
    case 'integer':
    case 'float':
    case 'boolean':
    case 'currency':
    case 'date':
    case 'dateTime':
    case 'percentage':
      fieldNames.push('defaultTo');
      break;
    case 'optionalString':
    case 'optionalInteger':
    case 'optionalFloat':
    case 'optionalBoolean':
    case 'optionalCurrency':
    case 'optionalDate':
    case 'optionalDateTime':
    case 'optionalPercentage':
      break;
    default:
      return finleyAssertUnreachable(type);
  }
  return fieldNames;
};

type BaseFieldSpecificValuesProp = {
  formProps: FormikProps<CreditAgreement.DataTapeField.BaseConfig>;
};
export const BaseFieldSpecificValues = ({
  formProps,
}: BaseFieldSpecificValuesProp) => {
  const dateFormatOptions = useMemo(() => generateDateFormats(), []);
  const getError = (fieldName: string) => {
    const errorMessage =
      fieldName in formProps.errors
        ? // @ts-ignore
          formProps.errors[fieldName]
        : undefined;

    return errorMessage;
  };
  const { values } = formProps;
  const { type } = values;
  switch (type) {
    case 'boolean':
    case 'optionalBoolean':
      return (
        <InputFormField
          type="text"
          propName="externalBooleanTrue"
          label="External Boolean True"
          formProps={formProps}
          value={values.externalBooleanTrue}
          placeholder="Enter externalBooleanTrue"
        />
      );
    case 'currency':
    case 'optionalCurrency':
      return (
        <InputFormField
          type="text"
          propName="currencyCode"
          label="Currency Code"
          formProps={formProps}
          value={values.currencyCode}
          placeholder="Enter currencyCode"
        />
      );
    case 'date':
    case 'optionalDate':
      return (
        <SelectWithCustomOption
          value={values.externalDateFormat}
          defaultCustomValue="yyyy-mm-dd"
          customInputLabel="Custom Date Format"
          options={dateFormatOptions}
          onChange={(updated: string) =>
            formProps.setFieldValue('externalDateFormat', updated)
          }
          errorMessage={getError('externalDateFormat')}
        />
      );
    case 'dateTime':
    case 'optionalDateTime':
      // @baseField_editor: use select for format and timzezone
      return (
        <>
          <InputFormField
            type="text"
            propName="externalDateTimeFormat"
            label="External Date Time Format"
            formProps={formProps}
            value={values.externalDateTimeFormat}
            placeholder="Enter externalDateTimeFormat"
          />
          <InputFormField
            type="text"
            propName="defaultTz"
            label="Default Time Zone"
            formProps={formProps}
            value={values.defaultTz}
            placeholder="Enter defaultTz"
          />
        </>
      );
    case 'string':
    case 'optionalString':
    case 'integer':
    case 'optionalInteger':
    case 'float':
    case 'optionalFloat':
    case 'percentage':
    case 'optionalPercentage':
      return null;
    default:
      return finleyAssertUnreachable(type);
  }
};

const validateBaseField = (
  values: CreditAgreement.DataTapeField.BaseConfig,
): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!values.field) {
    errors.field = 'Required';
  }
  if (!values.type) {
    errors.type = 'Required';
  }

  if (!DataTapeField.ValidTypes.includes(values.type)) {
    errors.type = 'Invalid type';
  }

  // validate type-specific fields
  const type = values.type;
  switch (type) {
    case 'boolean':
    case 'optionalBoolean':
      if (!values.externalBooleanTrue) {
        errors.externalBooleanTrue = 'Required';
      }
      break;
    case 'currency':
    case 'optionalCurrency':
      if (!values.currencyCode) {
        errors.currencyCode = 'Required';
      }
      if (!Bank.CurrencyCodes.includes(values.currencyCode)) {
        errors.currencyCode = 'Invalid currency code';
      }
      break;
    case 'date':
    case 'optionalDate':
      if (!values.externalDateFormat) {
        errors.externalDateFormat = 'Required';
      }
      break;
    case 'dateTime':
    case 'optionalDateTime':
      if (!values.externalDateTimeFormat) {
        errors.externalDateTimeFormat = 'Required';
      }

      if (!values.defaultTz) {
        errors.defaultTz = 'Required';
      }
      if (!_.values(Util.DateTimeZones).includes(values.defaultTz)) {
        errors.defaultTz = 'Invalid time zone';
      }
      break;
    case 'string':
    case 'optionalString':
    case 'integer':
    case 'optionalInteger':
    case 'float':
    case 'optionalFloat':
    case 'percentage':
    case 'optionalPercentage':
      break;
    default:
      finleyAssertUnreachable(type);
  }

  // validate DefaultTo values for non-optional fields
  switch (type) {
    case 'string':
    case 'integer':
    case 'float':
    case 'boolean':
    case 'currency':
    case 'date':
    case 'dateTime':
    case 'percentage':
      if (values.defaultTo !== undefined && values.defaultTo !== null) {
        // defaultTo is optional, but if is specified
        // it must be a valid value
        try {
          getSanitizedDataTapeField(values, values.defaultTo);
        } catch {
          const message = `Error parsing value`;
          errors.defaultTo = message;
        }
      }
      break;
    case 'optionalString':
    case 'optionalInteger':
    case 'optionalFloat':
    case 'optionalBoolean':
    case 'optionalCurrency':
    case 'optionalDate':
    case 'optionalDateTime':
    case 'optionalPercentage':
      break;
    default:
      finleyAssertUnreachable(type);
  }

  // if there are no errors
  // run typeguard
  if (Object.keys(errors).length === 0) {
    if (!typeguards.creditAgreement.isBaseFieldConfig(values)) {
      throw new Error('typeguard failed');
    }
  }

  return errors;
};

//
// Form for editing a base field
//

type BaseFieldFormProps = {
  formProps: FormikProps<CreditAgreement.DataTapeField.BaseConfig>;
  baseFieldType: CreditAgreement.DataTapeField.Type;
  onClose: () => void;
};
const BaseFieldForm = ({
  formProps,
  baseFieldType,
  onClose,
}: BaseFieldFormProps) => {
  useEffect(() => {
    // when user changes the baseFieldType
    // we want to delete values that are not relevent
    // to new types and reset the form
    const typeSpecificFieldNamesSet = new Set(
      getTypeSpecificFieldNames(baseFieldType),
    );
    allFieldNames.forEach((fieldName) => {
      if (!typeSpecificFieldNamesSet.has(fieldName)) {
        formProps.setFieldValue(fieldName, undefined);
        formProps.setFieldTouched(fieldName, false);
      }
    });
    formProps.setFieldValue('fieldName', undefined);

    // disabled eslint exhaustive deps. because
    // depending on formProps is not necessary
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseFieldType]);

  return (
    <Form onSubmit={formProps.handleSubmit}>
      <VStack spacing={5}>
        <InputFormField
          type="text"
          propName="field"
          label="Field"
          formProps={formProps}
          value={formProps.values.field}
          placeholder="Enter field"
        />
        <TextareaFormField
          propName="description"
          label="Description"
          formProps={formProps}
          value={formProps.values.description}
          placeholder="Enter description"
        />
        <SelectFormField
          name="type"
          label="Type"
          options={DataTapeField.ValidTypes.map((type) => ({
            value: type,
            label: type,
          }))}
        />
        <BaseFieldSpecificValues formProps={formProps} />
        <DefaultToValues formProps={formProps} />
      </VStack>

      <Divider mt="32px" mb="32px" />
      <Flex>
        <Spacer />
        <Button
          aria-label="Close"
          onClick={onClose}
          colorScheme="aurora"
          variant="outline"
          borderWidth="2px"
          mr="24px"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          aria-label="Save"
          isDisabled={false}
          colorScheme="aurora"
          color="white"
        >
          Save
        </Button>
      </Flex>
    </Form>
  );
};

//
// manages the modal, setups formoptions and validation
//
type BaseFieldEditorProps = {
  baseField: CreditAgreement.DataTapeField.BaseConfig;
  onUpdate: (
    updatedBaseField: CreditAgreement.DataTapeField.BaseConfig,
  ) => void;
  onClose: () => void;
};
export const BaseFieldEditor = ({
  baseField,
  onClose,
  onUpdate,
}: BaseFieldEditorProps) => {
  return (
    <Modal isOpen onClose={onClose}>
      <ModalOverlay />
      <ModalContent padding="20px">
        <ModalHeader>Edit BaseField</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Formik
            initialValues={baseField}
            onSubmit={(values) => {
              onUpdate(values);
              onClose();
            }}
            validate={(values) => {
              const errors = validateBaseField(values);
              return errors;
            }}
          >
            {(formikProps) => (
              <BaseFieldForm
                formProps={formikProps}
                baseFieldType={formikProps.values.type}
                onClose={onClose}
              />
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
